import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { MapPin, CheckCircle2, ChevronDown, ChevronUp, TrendingUp, Package, ArrowLeft } from 'lucide-react';
import OrderFilters, { DEFAULT_FILTERS, applyOrderFilters } from '../components/OrderFilters';
import EmptyStateCard from '../components/EmptyStateCard';
import { AVATAR_BG, AVATAR_FG } from '../constants/avatar';
import { formatOrderTime as formatTime } from '../utils/orderTime';
import { orderBarColor } from '../constants/deliveryStatus';

function dayLabel(dateStr) {
  if (!dateStr) return 'Date inconnue';
  const d = new Date(dateStr);
  const now = new Date();
  const yesterday = new Date(now); yesterday.setDate(now.getDate() - 1);
  if (d.toDateString() === now.toDateString()) return "Aujourd'hui";
  if (d.toDateString() === yesterday.toDateString()) return 'Hier';
  return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
}

function formatPhoneDisplay(value) {
  if (!value) return '—';
  const digits = String(value).replace(/\D/g, '');
  let local = digits;
  if (digits.length === 12 && digits.startsWith('221')) local = digits.slice(3);
  if (digits.length === 14 && digits.startsWith('00221')) local = digits.slice(5);
  if (local.length === 9) return `+221 ${local.slice(0, 2)} ${local.slice(2, 5)} ${local.slice(5, 7)} ${local.slice(7, 9)}`;
  if (digits.length > 0) return `+${digits}`;
  return String(value);
}

function groupByDay(items) {
  const groups = [];
  const map = new Map();
  for (const item of items) {
    // Grouper par date de livraison (delivered_at), fallback sur created_at
    const ref = item.delivered_at || item.created_at;
    const key = ref ? new Date(ref).toDateString() : 'unknown';
    if (!map.has(key)) {
      const entry = { label: dayLabel(ref), key, items: [] };
      map.set(key, entry);
      groups.push(entry);
    }
    map.get(key).items.push(item);
  }
  return groups;
}

function periodLabel(period, customFrom, customTo) {
  if (!period || period === 'all') return 'Toutes';
  if (period === 'today') return "Aujourd'hui";
  if (period === 'week') return 'Semaine';
  if (period === 'month') return 'Mois';
  if (period === 'custom') {
    if (customFrom && customTo) {
      const from = new Date(`${customFrom}T00:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
      const to = new Date(`${customTo}T00:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
      return `${from} → ${to}`;
    }
    return 'Période';
  }
  return 'Toutes';
}

export default function Traitees({ storeName = null, onBack = null }) {
  const PAGE_SIZE = 50;

  const [orders, setOrders]       = useState([]);
  const [drivers, setDrivers]     = useState([]);
  const [stores, setStores]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filters, setFilters]     = useState(DEFAULT_FILTERS);
  const [visibleCount, setVisibleCount] = useState(50);
  const [collapsed, setCollapsed] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    let query = supabase
      .from('deliveries')
      .select('id, client, phone, address, amount, delivery_fee, driver_id, status, created_at, delivered_at, versement_id, order_number, store, product_name, quantity')
      .eq('status', 'delivered')
      .not('versement_id', 'is', null)
      .order('delivered_at', { ascending: false, nullsFirst: false });

    if (storeName) query = query.eq('store', storeName);

    const [{ data: ord }, { data: drv }] = await Promise.all([
      query,
      supabase.from('drivers').select('id, name, color, initials'),
    ]);

    if (ord) {
      setOrders(ord);
      if (!storeName) {
        setStores([...new Set(ord.map(o => o.store).filter(Boolean))].sort());
      }
    }
    if (drv) setDrivers(drv);
    setLoading(false);
  }, [storeName]);

  useEffect(() => {
    load();
    let refreshTimer = null;
    const channel = supabase
      .channel('realtime:traitees')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'deliveries' }, () => {
        if (refreshTimer) clearTimeout(refreshTimer);
        refreshTimer = setTimeout(() => load(), 350);
      })
      .subscribe();
    return () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      supabase.removeChannel(channel);
    };
  }, [load]);

  const filtered        = applyOrderFilters(orders, filters, ['client', 'phone', 'address', 'order_number', 'store', 'product_name']);
  const visibleFiltered = filtered.slice(0, visibleCount);
  const grouped         = groupByDay(visibleFiltered);
  const hasActiveFilters = Boolean(
    filters.search?.trim()
    || (filters.period && filters.period !== 'all')
    || filters.customFrom
    || filters.customTo
    || filters.driverId
    || filters.storeId
  );
  const selectedPeriodLabel = periodLabel(filters.period, filters.customFrom, filters.customTo);

  const totalCA = filtered.reduce((s, o) => s + (parseFloat(o.amount) || 0), 0);

  const toggleDay = (key) => setCollapsed(c => ({ ...c, [key]: !c[key] }));

  return (
    <div className="page-enter">

      {/* ── En-tête ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '3px' }}>
        {onBack && (
          <button onClick={onBack} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '34px', height: '34px', borderRadius: '10px', border: '1.5px solid #e2e8f0', background: 'white', color: '#173b78', cursor: 'pointer', flexShrink: 0 }}>
            <ArrowLeft size={16} />
          </button>
        )}
        <h1 className="ui-title" style={{ margin: 0 }}>
          {storeName ? 'Commandes traitées' : 'Traitées'}
        </h1>
      </div>
      <p className="ui-subtitle" style={{ marginBottom: !loading ? '8px' : '20px' }}>
        {loading ? '…' : `${filtered.length} commande${filtered.length !== 1 ? 's' : ''} livrées et versées${filtered.length !== orders.length ? ` (sur ${orders.length})` : ''}`}
      </p>
      {!loading && (
        <div style={{ marginBottom: '16px', display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px 10px', borderRadius: '999px', border: '1px solid #bbf7d0', background: '#f0fdf4', color: '#15803d', fontSize: '10px', fontWeight: 700 }}>
          Période : {selectedPeriodLabel}
        </div>
      )}

      {/* ── Bannière stats ── */}
      {!loading && orders.length > 0 && (
        <div style={{ borderRadius: '18px', overflow: 'hidden', marginBottom: '20px', boxShadow: '0 8px 24px rgba(10,22,40,0.12)' }}>
          <div style={{ background: 'linear-gradient(135deg, #064e3b 0%, #15803d 100%)', padding: '18px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '1.5px', fontWeight: 700 }}>CA total versé</div>
              <div style={{ fontFamily: 'Alan Sans, sans-serif', fontSize: '28px', fontWeight: 800, color: 'white', lineHeight: 1.1, marginTop: '4px' }}>
                {totalCA.toLocaleString('fr-FR')} <span style={{ fontSize: '14px', opacity: 0.5, fontWeight: 600 }}>FCFA</span>
              </div>
            </div>
            <div style={{ width: '52px', height: '52px', borderRadius: '16px', background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircle2 size={24} color="white" />
            </div>
          </div>
          <div style={{ background: '#f0fdf4', borderTop: '1px solid #bbf7d0', display: 'grid', gridTemplateColumns: '1fr 1fr', padding: '0' }}>
            {[
              { label: 'Commandes traitées', value: filtered.length, icon: Package, color: '#15803d' },
              { label: 'CA moyen / commande', value: filtered.length > 0 ? `${Math.round(totalCA / filtered.length).toLocaleString('fr-FR')} F` : '—', icon: TrendingUp, color: '#0891b2' },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} style={{ padding: '12px 16px', borderRight: i === 0 ? '1px solid #bbf7d0' : 'none', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Icon size={14} color={s.color} />
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: 800, color: '#0f172a' }}>{s.value}</div>
                    <div style={{ fontSize: '10px', color: '#6b7280', fontWeight: 500 }}>{s.label}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Filtres ── */}
      <OrderFilters
        filters={filters}
        onChange={(updates) => { setFilters(f => ({ ...f, ...updates })); setVisibleCount(PAGE_SIZE); }}
        onReset={() => { setFilters(DEFAULT_FILTERS); setVisibleCount(PAGE_SIZE); }}
        drivers={drivers}
        stores={stores}
        showDriver={!storeName}
        showStore={!storeName && stores.length > 1}
        searchPlaceholder="Nom, adresse, boutique, produit..."
      />
      {!loading && orders.length > 0 && (
        <div style={{ marginTop: '-6px', marginBottom: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: '11px', color: '#64748b', fontWeight: 600 }}>
            {Math.min(visibleCount, filtered.length)}/{filtered.length} résultat{filtered.length > 1 ? 's' : ''}
          </span>
          {hasActiveFilters && (
            <span style={{ fontSize: '10px', color: '#15803d', fontWeight: 700, background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '3px 8px', borderRadius: '999px' }}>
              Filtres actifs
            </span>
          )}
        </div>
      )}

      {/* ── Contenu ── */}
      {loading ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {[0,1,2,3].map(i => (
            <div key={i} style={{ background: 'white', borderRadius: '14px', height: '72px', border: '1px solid #eef0f6', opacity: 0.4 + i * 0.15 }} />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyStateCard
          image="/images/stat-commandes-confirmees.webp"
          imageAlt="Aucune commande traitée"
          title={orders.length === 0 ? 'Aucune commande traitée' : 'Aucun résultat'}
          description={orders.length === 0 ? 'Les commandes livrées et versées apparaîtront ici.' : 'Essayez de modifier vos filtres.'}
          actionLabel={orders.length > 0 ? 'Réinitialiser les filtres' : undefined}
          onAction={orders.length > 0 ? () => setFilters(DEFAULT_FILTERS) : undefined}
          tone={orders.length > 0 ? 'success' : 'neutral'}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {grouped.map(group => {
            const isCollapsed = collapsed[group.key];
            const groupTotal  = group.items.reduce((s, o) => s + (parseFloat(o.amount) || 0), 0);
            return (
              <div key={group.key} style={{ background: 'white', borderRadius: '16px', border: '1px solid #e2f5ea', overflow: 'hidden', boxShadow: '0 2px 8px rgba(10,40,20,0.04)' }}>

                {/* Header jour */}
                <button
                  onClick={() => toggleDay(group.key)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '13px 16px', background: '#f0fdf4', border: 'none', cursor: 'pointer',
                    borderBottom: isCollapsed ? 'none' : '1px solid #d1fae5',
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: '32px', height: '32px', borderRadius: '10px', background: 'linear-gradient(135deg, #15803d, #166534)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 3px 8px rgba(21,128,61,0.3)' }}>
                      <CheckCircle2 size={14} color="white" />
                    </div>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: '13px', fontWeight: 700, color: '#064e3b' }}>{group.label}</div>
                      <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '1px' }}>
                        {group.items.length} commande{group.items.length !== 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '15px', fontWeight: 800, color: '#15803d' }}>
                        {groupTotal.toLocaleString('fr-FR')} F
                      </div>
                    </div>
                    <div style={{ width: '26px', height: '26px', borderRadius: '8px', background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid #bbf7d0' }}>
                      {isCollapsed
                        ? <ChevronDown size={13} color="#15803d" />
                        : <ChevronUp size={13} color="#15803d" />
                      }
                    </div>
                  </div>
                </button>

                {/* Liste commandes */}
                {!isCollapsed && (
                  <div style={{ padding: '6px 8px 8px' }}>
                    {group.items.map((o, i) => {
                      const driver = drivers.find(d => String(d.id) === String(o.driver_id));
                      const qty = Math.max(1, Number(o.quantity) || 1);
                      return (
                        <div key={o.id} style={{
                          display: 'flex', alignItems: 'center', gap: '10px',
                          padding: '10px 10px',
                          borderRadius: '10px',
                          background: i % 2 === 0 ? 'white' : '#fafbfd',
                          borderBottom: i < group.items.length - 1 ? '1px solid #f0fdf4' : 'none',
                        }}>
                          {/* Barre verte */}
                          <div style={{ width: '3px', alignSelf: 'stretch', borderRadius: '2px', background: orderBarColor(o), flexShrink: 0 }} />

                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '2px', flexWrap: 'wrap' }}>
                              <span style={{ fontWeight: 700, fontSize: '13px', color: '#0f172a' }}>{o.client}</span>
                              {o.order_number && (
                                <span style={{ fontSize: '10px', color: '#9ca3af', fontFamily: 'monospace' }}>#{o.order_number}</span>
                              )}
                              {o.store && !storeName && (
                                <span style={{ fontSize: '10px', fontWeight: 600, padding: '1px 6px', borderRadius: '5px', background: '#eff6ff', color: '#1d4ed8' }}>{o.store}</span>
                              )}
                            </div>
                            {o.product_name && (
                              <div style={{ fontSize: '11px', fontWeight: 700, color: '#173b78', marginBottom: '2px' }}>
                                {o.product_name}{qty > 1 ? ` ×${qty}` : ''}
                              </div>
                            )}
                            {o.address && (
                              <div style={{ fontSize: '11px', color: '#94a3b8', display: 'flex', alignItems: 'center', gap: '3px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                <MapPin size={9} /> {o.address}
                              </div>
                            )}
                            {o.phone && (
                              <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>
                                {formatPhoneDisplay(o.phone)}
                              </div>
                            )}
                            {driver && (
                              <div style={{ fontSize: '11px', color: '#1d4ed8', marginTop: '2px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <div style={{ width: '13px', height: '13px', borderRadius: '3px', background: AVATAR_BG, display: 'flex', alignItems: 'center', justifyContent: 'center', color: AVATAR_FG, fontSize: '7px', fontWeight: 700 }}>{driver.initials}</div>
                                {driver.name}
                              </div>
                            )}
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '3px', flexShrink: 0 }}>
                            {o.amount && (
                              <span style={{ fontSize: '13px', fontWeight: 800, color: '#15803d' }}>{Number(o.amount).toLocaleString('fr-FR')} F</span>
                            )}
                            <span style={{ fontSize: '10px', color: '#94a3b8' }}>{formatTime(o.created_at)}</span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', padding: '2px 7px', borderRadius: '20px', fontSize: '9px', fontWeight: 700, background: '#ecfdf5', color: '#047857', border: '1px solid #a7f3d0', boxShadow: '0 1px 2px rgba(4,120,87,0.08)' }}>
                              <CheckCircle2 size={8} /> Versée
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}

          {/* Charger plus */}
          {visibleCount < filtered.length && (
            <button
              onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
              style={{
                width: '100%', padding: '13px', borderRadius: '12px',
                border: '1.5px dashed #bbf7d0', background: '#f0fdf4',
                color: '#15803d', fontSize: '13px', fontWeight: 700,
                cursor: 'pointer', transition: 'all 0.15s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#dcfce7'; e.currentTarget.style.borderColor = '#86efac'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#f0fdf4'; e.currentTarget.style.borderColor = '#bbf7d0'; }}
            >
              Charger plus — {filtered.length - visibleCount} restante{filtered.length - visibleCount > 1 ? 's' : ''}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
